import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Link,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Block as BlockIcon,
  HourglassEmpty as HourglassEmptyIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../../store/store';
import {
  fetchMyEmails,
  fetchEmailDetails,
  fetchMyEmailStats,
  clearCurrentEmail,
  clearError,
} from '../../store/slices/myEmailsSlice';
import { ProcessedEmail } from '../../types/emailAccount';
import { useModernAlert } from '../../hooks/useModernAlert';

const MyEmails: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { emails, currentEmail, stats, loading, error, currentPage, totalPages } = useSelector(
    (state: RootState) => state.myEmails
  );
  const { showError, AlertComponent } = useModernAlert();

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchMyEmails({ page: 1 }));
    dispatch(fetchMyEmailStats());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      showError(error);
      dispatch(clearError());
    }
  }, [error, showError, dispatch]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    dispatch(fetchMyEmails({ page, status: statusFilter || undefined }));
  };

  const handleStatusFilterChange = (event: any) => {
    const status = event.target.value;
    setStatusFilter(status);
    dispatch(fetchMyEmails({ page: 1, status: status || undefined }));
  };

  const handleViewDetails = async (email: ProcessedEmail) => {
    try {
      await dispatch(fetchEmailDetails(email.id)).unwrap();
      setDetailsDialogOpen(true);
    } catch (err: any) {
      showError(err || 'Failed to load email details');
    }
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    dispatch(clearCurrentEmail());
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'completed':
        return <Chip icon={<CheckCircleIcon />} label="Completed" color="success" size="small" />;
      case 'skipped':
        return <Chip icon={<BlockIcon />} label="Skipped" color="default" size="small" />;
      case 'failed':
        return <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" />;
      case 'processing':
        return <Chip icon={<HourglassEmptyIcon />} label="Processing" color="primary" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        My Emails
      </Typography>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Emails
                </Typography>
                <Typography variant="h4">{stats.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Successfully Processed
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.completed}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Skipped
                </Typography>
                <Typography variant="h4">{stats.skipped}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Failed
                </Typography>
                <Typography variant="h4" color="error.main">
                  {stats.failed}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filter */}
      <Box sx={{ mb: 2 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Status Filter</InputLabel>
          <Select value={statusFilter} onChange={handleStatusFilterChange} label="Status Filter">
            <MenuItem value="">All</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="skipped">Skipped</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Emails Table */}
      {loading && !emails.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : emails.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" color="textSecondary" align="center">
              No emails found. Send an invoice PDF to the configured mailbox to see it here.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date Received</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>PDFs</TableCell>
                  <TableCell>Invoices</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emails.map((email) => (
                  <TableRow key={email.id}>
                    <TableCell>{formatDate(email.received_date)}</TableCell>
                    <TableCell>{email.message_subject}</TableCell>
                    <TableCell>{getStatusChip(email.processing_status)}</TableCell>
                    <TableCell>{email.attachment_count}</TableCell>
                    <TableCell>
                      {email.invoice_details && email.invoice_details.length > 0 ? (
                        email.invoice_details.map((invoice, idx) => (
                          <Link
                            key={invoice.id}
                            component="button"
                            variant="body2"
                            onClick={() => navigate(`/invoices`)}
                            sx={{ display: 'block' }}
                          >
                            {invoice.invoice_number}
                          </Link>
                        ))
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleViewDetails(email)} color="primary">
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} color="primary" />
            </Box>
          )}
        </>
      )}

      {/* Email Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        {currentEmail && (
          <>
            <DialogTitle>Email Details</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    From
                  </Typography>
                  <Typography>
                    {currentEmail.sender_name || currentEmail.sender_email} &lt;{currentEmail.sender_email}&gt;
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Subject
                  </Typography>
                  <Typography>{currentEmail.message_subject}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Received Date
                  </Typography>
                  <Typography>{formatDate(currentEmail.received_date)}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Status
                  </Typography>
                  {getStatusChip(currentEmail.processing_status)}
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    PDF Attachments
                  </Typography>
                  <Typography>{currentEmail.attachment_count}</Typography>
                </Box>

                {currentEmail.mailbox_name && (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Mailbox
                    </Typography>
                    <Typography>{currentEmail.mailbox_name}</Typography>
                  </Box>
                )}

                {currentEmail.invoice_details && currentEmail.invoice_details.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Created Invoices
                    </Typography>
                    {currentEmail.invoice_details.map((invoice) => (
                      <Card key={invoice.id} variant="outlined" sx={{ mb: 1, p: 1 }}>
                        <Typography variant="body2">
                          <strong>{invoice.invoice_number}</strong>
                        </Typography>
                        {invoice.supplier_name && (
                          <Typography variant="body2" color="textSecondary">
                            {invoice.supplier_name}
                          </Typography>
                        )}
                        <Typography variant="body2">
                          Amount: {invoice.total_amount} CZK
                        </Typography>
                      </Card>
                    ))}
                  </Box>
                )}

                {currentEmail.skip_reason && (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Skip Reason
                    </Typography>
                    <Typography color="warning.main">{currentEmail.skip_reason}</Typography>
                  </Box>
                )}

                {currentEmail.error_message && (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Error Message
                    </Typography>
                    <Typography color="error">{currentEmail.error_message}</Typography>
                  </Box>
                )}

                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Processing Timeline
                  </Typography>
                  <Typography variant="body2">
                    Started: {currentEmail.processing_started_at ? formatDate(currentEmail.processing_started_at) : '-'}
                  </Typography>
                  <Typography variant="body2">
                    Completed: {currentEmail.processing_completed_at ? formatDate(currentEmail.processing_completed_at) : '-'}
                  </Typography>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetails}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Alert Component */}
      {AlertComponent}
    </Box>
  );
};

export default MyEmails;

